// Exibição de grandezas. O banco guarda SI base (V, A, W); o prefixo e as casas decimais
// são preferência do usuário (Perfil → Unidades) — o mesmo conceito do UnitSettingsService
// do app GRTD-2000. Quem decide a categoria é a BASE de escala, não a unidade: corrente de
// armadura (Iarm) e de excitação (Ifld) têm prefixos independentes.

import type { UnitPrefs } from "./api";

type Quantity = "voltage" | "currentArm" | "currentFld" | "power" | null;

const QUANTITY_BY_BASE: Record<string, Quantity> = {
  Varm: "voltage",
  Vfld: "voltage",
  Iarm: "currentArm",
  Ifld: "currentFld",
  Pot: "power",
};

const FACTOR: Record<string, number> = { "": 1, k: 1e3, M: 1e6 };

export const DEFAULT_PREFS: Required<UnitPrefs> = {
  voltage: "kV",
  currentArm: "A",
  currentFld: "A",
  power: "MW",
  decimals: 2,
};

export interface Display {
  /** Divide o valor SI por isto. */
  factor: number;
  /** Unidade exibida (kV, MVAr, A, Hz, %...). */
  unit: string;
  decimals: number;
}

/** Como exibir uma grandeza de base/unidade SI dadas as preferências do usuário. */
export function displayFor(base: string | null | undefined, siUnit: string, prefs: UnitPrefs | undefined): Display {
  const p = { ...DEFAULT_PREFS, ...prefs };
  const q = base ? QUANTITY_BY_BASE[base] ?? null : null;
  if (!q) return { factor: 1, unit: siUnit, decimals: p.decimals };
  const chosen = p[q]; // ex.: "kV", "MW", "A"
  const prefix = chosen.length > 1 ? chosen[0]! : "";
  // Potência reativa: o prefixo escolhido vale, a unidade continua VAr.
  const unit = q === "power" ? `${prefix}${siUnit}` : chosen;
  return { factor: FACTOR[prefix] ?? 1, unit, decimals: p.decimals };
}

export function formatValue(value: number | null | undefined, d: Display): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return (value / d.factor).toLocaleString("pt-BR", {
    minimumFractionDigits: d.decimals,
    maximumFractionDigits: d.decimals,
  });
}

/** Rótulo curto do grupo de unidade para os painéis do gráfico (um eixo por painel). */
export function panelKey(base: string | null | undefined, d: Display): string {
  // Armadura e campo ficam em painéis separados mesmo com a mesma unidade: as escalas
  // diferem por duas ordens de grandeza (13,8 kV × 0,18 kV) e o campo sumiria achatado.
  if (base === "Varm") return `${d.unit} · armadura`;
  if (base === "Vfld") return `${d.unit} · campo`;
  if (base === "Iarm") return `${d.unit} · armadura`;
  if (base === "Ifld") return `${d.unit} · excitação`;
  return d.unit || "adimensional";
}

/**
 * Data e hora com milissegundos (oscilografia e SOE). Atenção: passar só
 * `fractionalSecondDigits` ao toLocaleString descarta as outras partes — daí listar todas.
 */
export function formatDateTimeMs(iso: string | number | Date): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });
}

/**
 * Número digitado em pt-BR ("13,8", "1.000", "23.900,5") ou com ponto decimal ("13.8").
 * O ponto só é separador de milhar quando há vírgula ou o formato é de milhar ("1.000"):
 * "13.8" é 13,8 e não 138.
 */
export function parseDecimal(text: string): number | null {
  const t = text.trim();
  if (!t) return null;
  const normalized = t.includes(",") || /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, "").replace(",", ".") : t;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}
